import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Loader from '../common/Loader';
import Menu from '../home1/Menu';
import Navbar from '../home1/Navbar';
import Footer2 from '../home2/Footer';
import { getStatusLabel, getTypeLabel } from '../../utils/projectTranslations';

function ProjectPage({ projectData }) {
  const { t, i18n } = useTranslation();

  // Get the current language
  const currentLanguage = i18n.language || 'en';
  
  // Get the description based on current language
  const getDescription = () => {
    if (typeof projectData.description === 'object') {
      return projectData.description[currentLanguage] || projectData.description['en'];
    }
    return projectData.description;
  };

  // Get the features based on current language
  const getFeatures = () => {
    if (typeof projectData.features === 'object') {
      return projectData.features[currentLanguage] || projectData.features['en'];
    }
    return projectData.features;
  };

  // Get the location based on current language
  const getLocation = () => {
    if (typeof projectData.location === 'object') {
      return projectData.location[currentLanguage] || projectData.location['en'];
    }
    return projectData.location;
  };

  // Get free apartments badge text
  const getFreeApartments = () => {
    // Check for availableApartments (numeric field)
    if (projectData.availableApartments !== undefined && projectData.availableApartments !== null) {
      const num = projectData.availableApartments;
      const translations = {
        'en': `${num} free apartment${num !== 1 ? 's' : ''}`,
        'bg': `${num} свободни апартамента`,
        'de': `${num} freie Wohnung${num !== 1 ? 'en' : ''}`,
        'ru': `${num} свободных квартир`
      };
      return translations[currentLanguage] || translations['en'];
    }
    // Fall back to freeApartments field
    if (projectData.freeApartments) {
      if (typeof projectData.freeApartments === 'object') {
        return projectData.freeApartments[currentLanguage] || projectData.freeApartments['en'];
      }
      return projectData.freeApartments;
    }
    return null;
  };

  // Get the map embed URL
  const getMapEmbedUrl = () => {
    const coords = projectData.mapData?.coordinates;
    if (coords?.lat && coords?.lng) {
      return `https://www.google.com/maps?q=${coords.lat},${coords.lng}&hl=${currentLanguage}&z=16&output=embed`;
    }
    return projectData.mapData?.embedUrl || "https://www.google.com/maps?q=Varna,Bulgaria&hl=bg&z=12&output=embed";
  };

  // Get the map directions URL
  const getMapDirectionsUrl = () => {
    return projectData.mapData?.directionsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(getLocation())}`;
  };

  return (
    <>
      <Helmet>
        <title>{projectData.title} - OneArt Construction</title>
        <meta name="description" content={getDescription()} />
        
        {/* CSS Styles */}
        <link rel="stylesheet" href="/home1/assets/css/home_1_style.css" />
        <link rel="stylesheet" href="/home2/assets/css/home_2_style.css" />
        <link rel="stylesheet" href="/innerpages/assets/css/innerpages.css" />
        
        {/* JS Scripts */}
        <script src="/common/assets/js/common_js.js" defer></script>
        <script src="/home1/assets/js/home_1_scripts.js" defer></script>
        <script src="/innerpages/assets/js/innerpages.js" defer></script>
        
        {/* Badge Animation Styles */}
        <style>{`
          @keyframes pulse {
            0% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.2);
              opacity: 0.8;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}</style>
      </Helmet>

      <div className="home-style1">
        <Loader />
        <Menu />
        <div className="smooth-scroll-content" id="scrollsmoother-container">
          <Navbar />
          <div style={{ position: 'relative', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
              <img src={projectData.heroImage} alt={projectData.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)' }}></div>
            </div>
            <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', color: 'white', padding: '60px 20px' }}>
              <nav aria-label="breadcrumb" style={{ marginBottom: '20px' }}>
                <ol style={{ display: 'flex', justifyContent: 'center', listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginRight: '8px' }}>
                    <a href="/" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>{t('projectPage.breadcrumb.home')}</a>
                  </li>
                  <li style={{ marginRight: '8px', color: 'rgba(255,255,255,0.7)' }}>/</li>
                  <li style={{ marginRight: '8px' }}>
                    <a href="/innerpages/portfolio" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>{t('projectPage.breadcrumb.projects')}</a>
                  </li>
                  <li style={{ marginRight: '8px', color: 'rgba(255,255,255,0.7)' }}>/</li>
                  <li style={{ color: 'white' }}>{projectData.title}</li>
                </ol>
              </nav>
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '20px', fontFamily: 'Montserrat, sans-serif' }}>
                {projectData.title}
              </h1>
              <p style={{ fontSize: '1.2rem', maxWidth: '800px', margin: '0 auto', lineHeight: '1.6', marginBottom: '20px' }}>
                {getLocation()}
              </p>
              {getFreeApartments() && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: 'transparent',
                  color: 'white',
                  padding: '12px 28px',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  marginTop: '15px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  transition: 'all 0.3s ease',
                  border: '2px solid rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <span style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    backgroundColor: '#4CAF50',
                    borderRadius: '50%',
                    marginRight: '10px',
                    boxShadow: '0 0 8px rgba(76, 175, 80, 0.8)',
                    animation: 'pulse 2s infinite'
                  }}></span>
                  {getFreeApartments()}
                </div>
              )}
            </div>
          </div>
          
          <main>
            {/* Azizi-style Project Hero */}
            {/* <section className="azizi-project-hero">
              <div className="hero-image">
                <img src={projectData.heroImage} alt={projectData.title} className="img-cover" />
                <div className="hero-overlay">
                  <div className="container">
                    <div className="hero-content">
                      <div className="breadcrumb">
                        <span>{t('projectPage.breadcrumb.home')}</span>
                        <span>/</span>
                        <span>{t('projectPage.breadcrumb.projects')}</span>
                        <span>/</span>
                        <span>{projectData.title}</span>
                      </div>
                      <h1 className="hero-title">{projectData.title}</h1>
                      <p className="hero-subtitle">{getLocation()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </section> */}

            {/* Project Overview Section */}
            <section className="azizi-project-overview">
              <div className="container">
                <div className="row">
                  <div className="col-lg-8">
                    <div className="overview-content">
                      <h2 className="section-title">{t('projectPage.overview.title')}</h2>
                      <p className="overview-description">{getDescription()}</p>
                      
                      <div className="project-features">
                        <h3 className="features-title">{t('projectPage.overview.keyFeatures')}</h3>
                        <div className="features-grid">
                          {getFeatures().map((feature, index) => (
                            <div key={index} className="feature-item">
                              <i className="la la-check"></i>
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-lg-4">
                    <div className="project-info-card">
                      <h3 className="info-title">{t('projectPage.info.title')}</h3>
                      <div className="info-list">
                        <div className="info-item">
                          <span className="info-label">{t('projectPage.info.location')}</span>
                          <span className="info-value">{getLocation()}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">{t('projectPage.info.type')}</span>
                          <span className="info-value">{getTypeLabel(projectData.type, t)}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">{t('projectPage.info.year')}</span>
                          <span className="info-value">{projectData.year}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">{t('projectPage.info.status')}</span>
                          <span className="info-value">{getStatusLabel(projectData.status, t)}</span>
                        </div>
                        <div className="info-item">
                          <span className="info-label">{t('projectPage.info.units')}</span>
                          <span className="info-value">{projectData.stats.units}</span>
                        </div>
                        {projectData.stats.greenAreas && (
                          <div className="info-item">
                            <span className="info-label">{t('projectPage.info.greenAreas')}</span>
                            <span className="info-value">{projectData.stats.greenAreas}</span>
                          </div>
                        )}
                        {projectData.stats.parkingSpaces && (
                          <div className="info-item">
                            <span className="info-label">{t('projectPage.info.parkingSpaces')}</span>
                            <span className="info-value">{projectData.stats.parkingSpaces}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="contact-cta">
                        <a href="/innerpages/contact" className="cta-button">
                          <span>{t('projectPage.info.getMoreInfo')}</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Project Gallery Section */}
            <section className="azizi-project-gallery" style={{ backgroundColor: 'white', padding: '80px 0' }}>
              <div className="container">
                <h2 className="section-title text-center">{t('projectPage.gallery.title')}</h2>
                <div className="gallery-grid">
                  <div className="row">
                    {projectData.images.map((image, index) => (
                      <div key={index} className="col-lg-4 col-md-6 mb-4">
                        <div className="gallery-item">
                          <img
                            src={image}
                            alt={`${projectData.title} - view ${index + 1}`}
                            className="img-cover"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Location & Amenities Section */}
            {/* <section className="azizi-location-amenities">
              <div className="container">
                <div className="row">
                  <div className="col-lg-6">
                    <div className="location-content">
                      <h3 className="section-title">{t('projectPage.location.title')}</h3>
                      <p className="location-description">
                        {t('projectPage.location.description', { location: getLocation() })}
                      </p>
                      <div className="location-features">
                        <div className="feature-item">
                          <i className="la la-map-marker"></i>
                          <span>{t('projectPage.location.features.primeLocation')}</span>
                        </div>
                        <div className="feature-item">
                          <i className="la la-car"></i>
                          <span>{t('projectPage.location.features.easyAccess')}</span>
                        </div>
                        <div className="feature-item">
                          <i className="la la-shopping-cart"></i>
                          <span>{t('projectPage.location.features.nearShopping')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-lg-6">
                    <div className="amenities-content">
                      <h3 className="section-title">{t('projectPage.amenities.title')}</h3>
                      <div className="amenities-grid">
                        <div className="amenity-item">
                          <i className="la la-swimming-pool"></i>
                          <span>{t('projectPage.amenities.swimmingPool')}</span>
                        </div>
                        <div className="amenity-item">
                          <i className="la la-dumbbell"></i>
                          <span>{t('projectPage.amenities.fitnessCenter')}</span>
                        </div>
                        <div className="amenity-item">
                          <i className="la la-car"></i>
                          <span>{t('projectPage.amenities.parking')}</span>
                        </div>
                        <div className="amenity-item">
                          <i className="la la-shield"></i>
                          <span>{t('projectPage.amenities.security')}</span>
                        </div>
                        <div className="amenity-item">
                          <i className="la la-tree"></i>
                          <span>{t('projectPage.amenities.gardens')}</span>
                        </div>
                        <div className="amenity-item">
                          <i className="la la-wifi"></i>
                          <span>{t('projectPage.amenities.internet')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section> */}

            {/* Google Maps Section */}
            <section className="azizi-map-section">
              <div className="container">
                <div className="map-header text-center">
                  <h3 className="section-title">{t('projectPage.map.title')}</h3>
                  <p className="map-description">
                    {t('projectPage.map.description')}
                  </p>
                </div>
                <div className="map-container">
                  <iframe
                    src={getMapEmbedUrl()}
                    width="100%"
                    height="450"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`${projectData.title} Location Map`}
                  ></iframe>
                </div>
                <div className="map-actions text-center">
                  <a 
                    href={getMapDirectionsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="directions-button"
                  >
                    <i className="la la-directions"></i>
                    <span>{t('projectPage.map.getDirections')}</span>
                  </a>
                </div>
              </div>
            </section>

            {/* Contact Section */}
            <section className="azizi-contact-section">
              <div className="container">
                <div className="contact-content text-center">
                  <h2 className="section-title">{t('projectPage.contact.title')}</h2>
                  <p className="contact-description">
                    {t('projectPage.contact.description')}
                  </p>
                  <div className="contact-buttons">
                    <a href="/innerpages/contact" className="primary-button">
                      <span>{t('projectPage.contact.contactSales')}</span>
                    </a>
                    <a href="/innerpages/portfolio" className="secondary-button">
                      <span>{t('projectPage.contact.viewAllProjects')}</span>
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </main>
          
          <Footer2 />
        </div>
      </div>
    </>
  );
}

export default ProjectPage;
